import type React from "react";
import { type ComponentProps, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../basic/Button";
import { Col, Row } from "react-bootstrap";
import { HStack } from "../../../../styled-system/jsx";
import { Modal } from "../basic/Modal";
import { css } from "../../../../styled-system/css";

interface Props {
  title?: string;
  confirmationMessage?: React.ReactNode;
  onConfirm: () => void;
  confirmButtonText: string;
  modalButtonText: React.ReactNode;
  loading?: boolean;
  loadingModalButtonText?: string;
  buttonStyle?: string;
  disableConfirmButton?: boolean;
  modelButtonStyle?: React.CSSProperties;
  modelButtonClass?: string;
  disabled?: boolean;
  inlineModal?: boolean;
  onCancel?: () => void;
  inlineButtonContainerStyles?: React.CSSProperties;
  defaultShowState?: boolean;
  cancelButtonStyles?: React.CSSProperties;
  confirmButtonStyles?: React.CSSProperties;
  hideCancelButton?: boolean;
  children?: React.ReactNode;
  modalButtonProps?: Omit<ComponentProps<typeof Button>, "children">;
}

const ConfirmationModal = ({
  title,
  confirmationMessage,
  onConfirm,
  confirmButtonText,
  modalButtonText,
  loading,
  disabled = false,
  loadingModalButtonText,
  disableConfirmButton = false,
  modelButtonStyle = { marginRight: "2rem" },
  modalButtonProps,
  inlineModal = false,
  onCancel,
  inlineButtonContainerStyles,
  defaultShowState = false,
  cancelButtonStyles = {},
  confirmButtonStyles = {},
  hideCancelButton = false,
  children,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const [showModal, setShowModal] = useState(defaultShowState);

  const modalButtonTextToDisplay = loading ? loadingModalButtonText : modalButtonText;

  return (
    <>
      {showModal && (
        <Modal
          isOpen={showModal}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setShowModal(false);
              onCancel?.();
            }
          }}
          title={title ? title : ""}
          css={{ display: "flex", flexDirection: "column", width: "60vw", maxHeight: "90vh" }}
        >
          {" "}
          <div
            className={css({
              flex: "1 1 auto",
              padding: "20px 10px",
              textAlign: "center",
              overflowY: "auto",
            })}
          >
            <Row
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px 10px",
                textAlign: "center",
              }}
            >
              <Col style={{ margin: "0 20px" }} xs={12}>
                {confirmationMessage}
              </Col>
              {children}
            </Row>
          </div>
          <HStack justifyContent="space-around">
            {!hideCancelButton && (
              <Button
                variant={"secondary"}
                onPress={() => {
                  setShowModal(false);
                  onCancel?.();
                }}
                style={cancelButtonStyles}
              >
                {t(($) => $.common.actions["Cancel"])}
              </Button>
            )}
            <Button
              onPress={() => {
                onConfirm();
                setShowModal(false);
              }}
              isDisabled={disableConfirmButton}
              style={confirmButtonStyles}
            >
              {confirmButtonText}
            </Button>
          </HStack>
        </Modal>
      )}

      {!inlineModal ?
        <Button
          onPress={() => setShowModal(!showModal)}
          isDisabled={disabled}
          style={modelButtonStyle}
          {...modalButtonProps}
        >
          {modalButtonTextToDisplay}
        </Button>
      : <div
          style={
            inlineButtonContainerStyles ? inlineButtonContainerStyles : (
              { width: "100%", height: "100%", textAlign: "left" }
            )
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowModal(true);
          }}
        >
          {modalButtonTextToDisplay}
        </div>
      }
    </>
  );
};

export default ConfirmationModal;
